// Argo observed profile — the in-situ counterpart to the modelled SVP curve.
//
// Les Bender's current Argo workflow (SVP_bender/SVProfile_ARGO.m) is a manual
// ordeal: euro-argo web UI → email request → seven progress emails from Ifremer →
// download link → WinZip → a 30-line NetCDF reader (T / S / pressure → depth →
// profile). This module replaces the ACQUISITION with a live ERDDAP tabledap
// query against the Coriolis Global Data Assembly Centre, so the console can draw
// a real float cast beside the model without anyone opening an inbox.
//
// The pattern this serves is the console's oldest rule: OBSERVED sits BESIDE
// MODELLED, never blended, always with provenance. This module only fetches and
// selects the observation; the SVP panel draws it as a second, visually distinct
// curve on the same axes and labels it with the float, its age, and its distance.
//
// Endpoint — Ifremer ERDDAP `ArgoFloats` (erddap.ifremer.fr), the canonical global
// Argo table (what euro-argo/Coriolis serve; what Les already receives by email).
// Verified empirically (2026-07-20):
//   - CORS: ERDDAP answers with `Access-Control-Allow-Origin: *`, so this fetches
//     client-direct like NDBC/Open-Meteo — no Go proxy. (Confirmed against the
//     Marine-Institute mirror, identical ERDDAP software, while Ifremer itself was
//     in a maintenance window; the schema and headers are the same Coriolis GDAC
//     stack.)
//   - NO-MATCH is HTTP 404 with a body reading "…(nRows = 0)", NOT 204. A query
//     that finds no float near a site therefore looks like a 404, and we read that
//     as a fact about the ocean (no float here), not a failure of the pipe.
//   - QC flags come back as STRING digits ("1","2","4"…), Argo reference table 2a.
// NOAA's OSMC ERDDAP is NOT an independent fallback — its ArgoFloats data queries
// 302-redirect to Ifremer — and the Marine-Institute mirror omits the Gulf of
// Mexico (lon ≥ −60.6°), a core TDI region, so a single canonical endpoint is the
// honest choice. On transport failure the caller keeps the overlay it already has.

/** One usable observed cast: a float, when and where it surfaced, and its levels.
 *  Sound speed is deliberately NOT stored here — see the note on `levels`. */
export interface ArgoProfile {
  /** WMO float identifier, e.g. "6901926". */
  platform: string;
  /** Profile time (epoch ms, UTC). Age is computed at render so the label stays
   *  live on a console left open. */
  timeUtcMs: number;
  /** Where the float surfaced for this cycle. */
  lat: number;
  lon: number;
  /** Great-circle distance from the clicked site to the float, nm. */
  distanceNm: number;
  /**
   * Depth-ordered levels (shallow → deep). Only depth / temperature / salinity —
   * NOT sound speed. The SVP panel computes SV for BOTH the modelled and the
   * observed curve with the same `soundSpeed(eq, …)` under the equation the
   * surveyor has selected; that is the whole point of the "one equation" rule —
   * if the observed curve used a different equation than the modelled one on
   * screen, the gap between them would be an artefact of the maths, not the sea.
   * (The stored SVP grid was built with Mackenzie 1981 in db/svp/fetch_svp.py,
   * but the panel recomputes both curves from T/S/D, so Mackenzie is not the
   * displayed equation and must not be hard-coded here.)
   */
  levels: { depthM: number; tempC: number; salPSU: number }[];
  /** Deepest usable level, m. The panel clamps the shared axis to the modelled
   *  range (≤1000 m); this lets the label note that the float went deeper. */
  maxDepthM: number;
}

/**
 * The outcome of asking ERDDAP for a nearby float, with the two absences kept
 * apart — the same discipline as FieldFetch in fieldmesh.ts, and for the same
 * reason: a dropped request must not look like an empty ocean.
 *
 *   { ok: true, profile }        — a usable float cast within range.
 *   { ok: true, profile: null }  — the source answered, but no float qualifies
 *                                  near this site. A true statement about the
 *                                  ocean; NOT a failure. (ERDDAP's 404/nRows=0.)
 *   { ok: false }                — we failed to ASK (network, 5xx, unparseable).
 *                                  We know nothing new, so the caller keeps the
 *                                  overlay it holds and tries again next click.
 */
export type ArgoFetch =
  | { ok: true; profile: ArgoProfile | null }
  | { ok: false };

// Through OUR server, not client-direct. The overlay shipped talking straight to
// Ifremer on the strength of a CORS check made against a mirror during Ifremer's
// maintenance window; the canonical host, it turns out, sends no CORS headers at
// all, so in production every browser fetch died at the CORS gate while the
// service was healthy — 8/8 "transport failures" against an upstream that was UP.
// The relay (server/argo.go) builds the identical query and preserves ERDDAP's
// 404-means-empty-ocean, so nothing below this line changed behaviour.
const RELAY = "/api/argo";

/** Half-width of the search box, degrees — a ~2° box around the site, as agreed. */
const BOX_DEG = 1.0;
/** How far back to look. Floats cycle ~10-daily, so 30 days is ~3 cycles: enough
 *  that a site almost always has a recent neighbour, without dragging in casts so
 *  old they describe a different season. */
const WINDOW_DAYS = 30;
/** Deepest pressure we ask for. Core Argo profiles run to ~2000 dbar; the cap
 *  bounds the payload without clipping a standard cast. */
const MAX_PRES_DBAR = 2010;
/** A profile with fewer good levels than this is not a profile — it cannot draw a
 *  curve worth standing beside the model. */
const MIN_LEVELS = 5;
/**
 * Recency's price, in nm per day, when choosing among candidate profiles.
 *
 * The nearest float is not always the best witness: a cast 20 nm away but 25 days
 * old can describe the water here worse than a fresh one 90 nm away, because
 * mesoscale features and the seasonal cycle move under the float. So we rank by a
 * cost = distanceNm + AGE_WEIGHT_NM_PER_DAY·ageDays and take the minimum. At 2
 * nm/day a 30-day-old cast carries a 60 nm penalty — i.e. a cast from today wins
 * unless it is more than 60 nm farther out. Judgement, not a law; documented so it
 * can be argued with. Distance still dominates for casts of similar age.
 */
const AGE_WEIGHT_NM_PER_DAY = 2;
/** Argo good / probably-good (reference table 2a). We keep only these. */
const GOOD_QC = new Set(["1", "2"]);

const EARTH_RADIUS_NM = 3440.065;
const toRad = (d: number): number => (d * Math.PI) / 180;

/** Great-circle distance in nm. Mirror of primer.ts's haversine (not exported
 *  there); a few lines is cheaper than reaching across modules for it. */
function haversineNm(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return EARTH_RADIUS_NM * 2 * Math.asin(Math.min(1, Math.sqrt(a)));
}

/**
 * Pressure (dbar) → depth (m), UNESCO / Fofonoff & Millard 1983 — the same
 * conversion as CSIRO SeaWater's sw_dpth, which Les's SVProfile_ARGO.m calls. The
 * gravity term is latitude-dependent, so this needs the float's own latitude.
 * (Fofonoff, N.P. and Millard, R.C. 1983, UNESCO Tech. Papers in Marine Sci. 44.)
 */
function pressureToDepthM(pressureDbar: number, latDeg: number): number {
  const P = pressureDbar;
  const sin2 = Math.sin(toRad(latDeg)) ** 2;
  const gPhi = 9.780318 * (1 + (5.2788e-3 + 2.36e-5 * sin2) * sin2) + 1.092e-6 * P;
  return (((((-1.82e-15 * P + 2.279e-10) * P - 2.2512e-5) * P + 9.72659) * P)) / gPhi;
}

interface RawTable {
  columnNames: string[];
  rows: unknown[][];
}

function asTable(json: unknown): RawTable | null {
  if (typeof json !== "object" || json === null) return null;
  const t = (json as { table?: unknown }).table;
  if (typeof t !== "object" || t === null) return null;
  const names = (t as { columnNames?: unknown }).columnNames;
  const rows = (t as { rows?: unknown }).rows;
  if (!Array.isArray(names) || !Array.isArray(rows)) return null;
  return { columnNames: names.map(String), rows: rows as unknown[][] };
}

const numOrNull = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

/**
 * Fetch the nearest usable Argo profile to a site. Client-direct to Ifremer
 * ERDDAP; see the module header for the endpoint, CORS and no-match facts.
 *
 * This does not report health or guard request order — that is the caller's job,
 * which already owns the request-id guard for the SVP click and the Systems row.
 */
export async function fetchArgo(lat: number, lon: number): Promise<ArgoFetch> {
  // The relay owns the query grammar (box, window, pressure cap, orderBy — kept
  // in lockstep with the constants above, which the containment cache still
  // reasons with); the response is ERDDAP's table JSON verbatim.
  const q = `${RELAY}?lat=${lat.toFixed(4)}&lon=${lon.toFixed(4)}`;

  let res: Response;
  try {
    // ERDDAP can be slow under load; a stuck request would otherwise hold the old
    // overlay forever. The timeout turns that into an ordinary transport failure,
    // which the caller handles by keeping what it has. The relay's own upstream
    // timeout is 35 s, so ours must be longer or we abandon answers in flight.
    res = await fetch(q, { signal: AbortSignal.timeout(40_000) });
  } catch {
    return { ok: false }; // network dropped, aborted, or timed out — ask again later
  }
  // 404 is ERDDAP's answer for "your query matched no rows" (…nRows = 0). That is a
  // real, authoritative statement that there is no float in this box/window — an
  // absence, not a fault. Any other non-ok status is a failure to ask.
  if (res.status === 404) return { ok: true, profile: null };
  if (!res.ok) return { ok: false };

  let json: unknown;
  try {
    json = await res.json();
  } catch {
    return { ok: false };
  }
  const table = asTable(json);
  if (table === null) return { ok: false }; // shape we don't recognise — a fault, not an empty sea

  const idx = (name: string): number => table.columnNames.indexOf(name);
  const iPlat = idx("platform_number");
  const iTime = idx("time");
  const iLat = idx("latitude");
  const iLon = idx("longitude");
  const iPres = idx("pres");
  const iTemp = idx("temp");
  const iPsal = idx("psal");
  const iTempQc = idx("temp_qc");
  const iPsalQc = idx("psal_qc");
  const iPosQc = idx("position_qc");
  if ([iPlat, iTime, iLat, iLon, iPres, iTemp, iPsal, iTempQc, iPsalQc, iPosQc].some((i) => i < 0)) {
    return { ok: false }; // a column we depend on is missing — treat as a fault
  }

  // Cut the ordered table into profiles keyed by (float, cycle-time). Rows failing
  // temp/psal QC are dropped at the level; the profile position's own QC gates the
  // whole cast (a mislocated float would place the curve — and its distance — in
  // the wrong place). Zero good rows produces no candidate at all.
  interface Cand {
    platform: string;
    time: string;
    lat: number;
    lon: number;
    levels: { depthM: number; tempC: number; salPSU: number }[];
  }
  const cands: Cand[] = [];
  let cur: Cand | null = null;
  let curKey = "";
  for (const row of table.rows) {
    const platform = String(row[iPlat] ?? "");
    const time = String(row[iTime] ?? "");
    const key = `${platform}|${time}`;
    if (key !== curKey) {
      if (cur !== null && cur.levels.length >= MIN_LEVELS) cands.push(cur);
      curKey = key;
      const plat = numOrNull(row[iLat]);
      const plon = numOrNull(row[iLon]);
      const posOk = GOOD_QC.has(String(row[iPosQc] ?? ""));
      cur = plat !== null && plon !== null && posOk
        ? { platform, time, lat: plat, lon: plon, levels: [] }
        : null; // a badly-located cycle contributes no levels and no candidate
    }
    if (cur === null) continue;
    const pres = numOrNull(row[iPres]);
    const temp = numOrNull(row[iTemp]);
    const psal = numOrNull(row[iPsal]);
    if (pres === null || temp === null || psal === null) continue;
    if (!GOOD_QC.has(String(row[iTempQc] ?? "")) || !GOOD_QC.has(String(row[iPsalQc] ?? ""))) continue;
    cur.levels.push({ depthM: pressureToDepthM(pres, cur.lat), tempC: temp, salPSU: psal });
  }
  if (cur !== null && cur.levels.length >= MIN_LEVELS) cands.push(cur);

  if (cands.length === 0) return { ok: true, profile: null }; // rows came back, none usable — still "no profile here"

  // Choose by cost = distance + recency penalty (see AGE_WEIGHT_NM_PER_DAY).
  const now = Date.now();
  let best: ArgoProfile | null = null;
  let bestCost = Infinity;
  for (const c of cands) {
    const timeUtcMs = Date.parse(c.time);
    if (!Number.isFinite(timeUtcMs)) continue;
    const distanceNm = haversineNm(lat, lon, c.lat, c.lon);
    const ageDays = Math.max(0, (now - timeUtcMs) / 86_400_000);
    const cost = distanceNm + AGE_WEIGHT_NM_PER_DAY * ageDays;
    if (cost >= bestCost) continue;
    const levels = c.levels.slice().sort((a, b) => a.depthM - b.depthM);
    bestCost = cost;
    best = {
      platform: c.platform,
      timeUtcMs,
      lat: c.lat,
      lon: c.lon,
      distanceNm,
      levels,
      maxDepthM: levels[levels.length - 1].depthM,
    };
  }
  return { ok: true, profile: best };
}

/** Days between a profile's cast time and now (for the label and freshness test). */
export function argoAgeDays(p: ArgoProfile, nowMs: number = Date.now()): number {
  return Math.max(0, (nowMs - p.timeUtcMs) / 86_400_000);
}

/** How far a new site can drift from the held float before a refetch is worth it.
 *  Within this the same float is still a near neighbour, so a click nearby reuses
 *  the profile in hand rather than hitting ERDDAP again. */
const REUSE_NM = 60;
/** Beyond this a held profile is too old to keep showing without re-checking —
 *  matches the Systems "argo" data-age budget so the two never disagree. */
const REUSE_MAX_AGE_MS = 35 * 86_400_000;

/** True when the profile in hand still represents `(lat, lon)` well enough to skip
 *  a refetch: same float still near, and the cast still within the age budget. */
export function argoStillRepresents(p: ArgoProfile, lat: number, lon: number): boolean {
  return (
    haversineNm(lat, lon, p.lat, p.lon) <= REUSE_NM &&
    argoAgeDays(p) * 86_400_000 <= REUSE_MAX_AGE_MS
  );
}
